import { Injectable } from '@angular/core';
import { ApplicationSettings } from 'projects/lib_config/src/public-api';
import { PublicCookieHelper } from '../helpers/public-cookie.helper';
import { Settings } from '../settings/settings';
import { OidcSecurityService } from './oidc-security.service';

@Injectable({
  providedIn: 'root'
})
export class SilentRenewService {
  private readonly calculatedThreshold: number;

  private silentRenewTimeout: Number | null;
  private previousTokenExpiration: Date;

  constructor(private oidcSecurityService: OidcSecurityService) {
    this.calculatedThreshold = Settings.SilentRenew.ThresholdInSeconds.createThresholdWithVariance();
  }

  public async startNew() {
    let publicCookie = await this.oidcSecurityService.tokenStatus().toPromise();
    PublicCookieHelper.create(publicCookie);

    this.previousTokenExpiration = publicCookie?.tokenExpiration;

    if (PublicCookieHelper.isValid(publicCookie)) {
      let expirationMinusSafetyTime = PublicCookieHelper.getExpirationMinusSafetyTime(publicCookie, Settings.SilentRenew.TimeoutInSeconds.safetyTime)
      if (new Date().getTime() >= expirationMinusSafetyTime) { //if cookie is already expired when starting up, check renew first and then start renew interval
        await this.silentRenewCheck(); 
      } else { // if token is still valid, start renew interval as normal
        this.createSilentRenewTimeout(publicCookie.tokenExpiration);
      }
    }
  }

  private createSilentRenewTimeout(tokenExpiration: Date) {
    if (!this.silentRenewTimeout) {
      this.previousTokenExpiration = tokenExpiration;
      this.silentRenewTimeout = window.setTimeout(() => this.silentRenewCheck(), this.calculateDelay(tokenExpiration));
    }
  }

  private async silentRenewCheck() {
    this.silentRenewTimeout = null;

    let publicCookie = PublicCookieHelper.get();
    if (PublicCookieHelper.isValid(publicCookie)) {
      if (publicCookie.tokenExpiration.getTime() !== this.previousTokenExpiration.getTime()) {
        this.createSilentRenewTimeout(publicCookie.tokenExpiration);
      } else { 
        await this.renewToken();
      }
    }
  }

  private async renewToken(){
    let tokenRenewResult = await this.oidcSecurityService.renewToken().toPromise();
    
    if (!tokenRenewResult.isError) {
      PublicCookieHelper.create({isAuthenticated: true, tokenExpiration: tokenRenewResult.tokenExpiration});
      this.createSilentRenewTimeout(new Date(tokenRenewResult.tokenExpiration));
    } else {
      //tokenRenew contained an error, if cookie was untouched we will auto login guard do his job.
      let publicCookie = PublicCookieHelper.get();
      if (publicCookie.tokenExpiration !== this.previousTokenExpiration) {
        this.createSilentRenewTimeout(publicCookie.tokenExpiration);
      }
    }
  }

  private calculateDelay(tokenExpiration: Date): number {
    let delayInMilliseconds = (tokenExpiration.getTime() - new Date().getTime()) - (this.calculatedThreshold * 1000);
    let minimumDelayInMilliseconds = Settings.SilentRenew.TimeoutInSeconds.minimumDelay * 1000;

    return delayInMilliseconds > minimumDelayInMilliseconds ? delayInMilliseconds : minimumDelayInMilliseconds;
  }
}

